import {
  Sun,
  Fuel,
  DollarSign,
  CircleDollarSign,
  TrendingUp,
  TrendingDown,
  Minus,
  Zap,
  BarChart3,
  Battery,
  ArrowDownToLine,
  ArrowUpFromLine,
  TreePine,
  Clock,
  Cog,
  Unplug,
  Leaf,
  Truck,
} from 'lucide-react';
import { InfoTooltip } from '@/components/ui/info-tooltip';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { GroupAnalyticsData } from '@/types/reports';
import { GRID_IMPORT_RATE_PER_KWH, GRID_SELL_RATE_PER_KWH } from '@/types/reports';
import { cn } from '@/lib/utils';

interface SummaryReportViewProps {
  data: GroupAnalyticsData | null;
  /** Dynamic label for group (Production vs Group based on industry) */
  groupLabel?: string;
}

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatKwh(value: number): string {
  if (Number.isNaN(value)) return '—';
  if (value >= 1_000_000) return `${(value / 1_000_000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} GWh`;
  // Show up to 6 digits in kWh (e.g., 999,999 kWh)
  return `${Math.round(value).toLocaleString()} kWh`;
}

function SummaryKpiCard({
  icon: Icon,
  iconColor,
  label,
  value,
  delta,
  subtitle,
}: {
  icon: React.ElementType;
  iconColor: string;
  label: string;
  value: string;
  delta?: number;
  subtitle?: string;
}) {
  const DeltaIcon = delta !== undefined && delta > 0 ? TrendingUp : delta !== undefined && delta < 0 ? TrendingDown : Minus;
  const deltaColor = delta !== undefined && delta > 0 ? 'text-[#8FB560]' : delta !== undefined && delta < 0 ? 'text-amber-500' : 'text-muted-foreground';
  const deltaBgColor = delta !== undefined && delta > 0 ? 'bg-[#8FB560]/10' : delta !== undefined && delta < 0 ? 'bg-amber-500/10' : 'bg-muted/50';

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        <div className="flex items-stretch min-h-[72px]">
          <div
            className="flex items-center justify-center w-14 shrink-0"
            style={{ backgroundColor: `${iconColor}15` }}
          >
            <Icon className="h-6 w-6" style={{ color: iconColor }} />
          </div>

          <div className="flex-1 p-2 flex flex-col justify-center min-w-0 relative">
            {/* Delta chip - Top right */}
            {delta !== undefined && (
              <span className={cn(
                "absolute top-1.5 right-1.5 text-[10px] font-semibold tabular-nums inline-flex items-center gap-0.5 px-1 py-0.5 rounded-full",
                deltaColor,
                deltaBgColor
              )}>
                <DeltaIcon className="h-2.5 w-2.5" />
                {delta > 0 ? '+' : ''}{delta.toFixed(1)}%
              </span>
            )}

            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
              {label}
            </span>

            {/* Value - Centered */}
            <div className="flex items-baseline justify-center mt-0.5">
              <span className="text-lg font-bold tabular-nums">{value}</span>
            </div>

            {subtitle && (
              <span className="text-[10px] text-muted-foreground/70 mt-0.5 text-center">
                {subtitle}
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function SummaryReportView({ data, groupLabel = 'production' }: SummaryReportViewProps) {
  if (!data || data.timeSeries.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <div className="text-muted-foreground">
            <Sun className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>Select a {groupLabel.toLowerCase()} to view summary</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { stats, energyMix, unitsInGroup, dateRange, timeSeries } = data;
  const _dateRangeLabel = `${formatDate(dateRange.start)} – ${formatDate(dateRange.end)}`;
  const gridSellRevenue = stats.gridSellRevenue ?? 0;
  const totalProfit = stats.totalProfit ?? (stats.totalCostSaved + gridSellRevenue);
  
  // Grid economics calculations for Period Insights
  // Use effective rate if provided, otherwise fall back to default
  const effectiveGridImportRate = data.effectiveRate?.gridImportRate ?? GRID_IMPORT_RATE_PER_KWH;
  const effectiveGridSellRate = data.effectiveRate?.gridSellRate ?? GRID_SELL_RATE_PER_KWH;
  const gridCost = Math.round(energyMix.gridKwh * effectiveGridImportRate);
  const totalValue = stats.totalCostSaved + gridSellRevenue - gridCost;
  const gridDependency = 100 - stats.avgOffGrid;
  
  // From timeSeries if available
  const maxUnitsActive = timeSeries?.length 
    ? Math.max(...timeSeries.map(t => t.unitsActive || 0))
    : unitsInGroup;
  const avgBatterySoc = stats.avgBatterySoc ?? 0;

  return (
    <div className="space-y-4">
      {/* KPI Row - Hard totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryKpiCard
          icon={Sun}
          iconColor="#f59e0b"
          label="Total Solar"
          value={stats.totalSolarKwh >= 1_000_000
            ? `${(stats.totalSolarKwh / 1_000_000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} GWh`
            : `${Math.round(stats.totalSolarKwh).toLocaleString()} kWh`}
          delta={stats.priorPeriodComparison.solarKwhDelta ?? undefined}
          subtitle={`${stats.dailyAvgSolarKwh.toLocaleString()} kWh/day avg`}
        />
        <SummaryKpiCard
          icon={Unplug}
          iconColor="#8FB560"
          label="Off-Grid"
          value={`${stats.avgOffGrid}%`}
          delta={stats.priorPeriodComparison.offGridDelta ?? undefined}
          subtitle={`Peak: ${stats.bestPeriod.value}%`}
        />
        <SummaryKpiCard
          icon={Leaf}
          iconColor="#10b981"
          label="CO₂ Reduced"
          value={`${Math.round(stats.totalCO2).toLocaleString()} kg`}
          delta={stats.priorPeriodComparison.co2Delta ?? undefined}
          subtitle={`≈ ${stats.treesEquivalent.toLocaleString()} trees (est.)`}
        />
        <SummaryKpiCard
          icon={DollarSign}
          iconColor="#8FB560"
          label="Cost Saved"
          value={`$${stats.totalCostSaved.toLocaleString()}`}
          subtitle={`${stats.dieselGallonsReduced.toLocaleString()} gal diesel`}
        />
      </div>

      {/* Rate Info Banner */}
      <div className="flex items-center gap-3 px-4 py-2 bg-muted/50 rounded-lg border border-border">
        <Zap className="h-4 w-4 text-[#8FB560] flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium">
              {data.effectiveRate?.rateName ?? 'System Default Rate'}
            </span>
            {data.effectiveRate?.utilityProvider && (
              <Badge variant="outline" className="text-[10px]">
                {data.effectiveRate.utilityProvider}
              </Badge>
            )}
            {data.effectiveRate?.nemVersion && (
              <Badge variant="outline" className="text-[10px]">
                {data.effectiveRate.nemVersion}
              </Badge>
            )}
            <Badge variant="secondary" className="text-[10px]">
              {data.effectiveRate?.rateSource
                ? `${data.effectiveRate.rateSource.charAt(0).toUpperCase()}${data.effectiveRate.rateSource.slice(1)} Rate`
                : 'Default'}
            </Badge>
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Import: ${effectiveGridImportRate.toFixed(2)}/kWh • Sell: ${effectiveGridSellRate.toFixed(2)}/kWh
            {data.effectiveRate?.sourceName && (
              <span className="ml-2">• via {data.effectiveRate.sourceName}</span>
            )}
          </div>
        </div>
        <InfoTooltip
          content={
            <div className="space-y-1 text-xs">
              <p className="font-semibold">Rate Used for Calculations</p>
              <p>Grid Import: ${effectiveGridImportRate.toFixed(2)}/kWh</p>
              <p>Grid Sell: ${effectiveGridSellRate.toFixed(2)}/kWh</p>
              {data.effectiveRate?.dieselCostPerGallon && (
                <p>Diesel: ${data.effectiveRate.dieselCostPerGallon.toFixed(2)}/gal</p>
              )}
              <p className="text-muted-foreground mt-2">
                {data.effectiveRate
                  ? 'Using assigned rate profile'
                  : 'Using system default rate'}
              </p>
            </div>
          }
        />
      </div>

      {/* Performance highlights & energy mix */}
      <div className="grid lg:grid-cols-2 gap-3">
        <Card className="h-full">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-[#8FB560]" />
              Performance Highlights
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div className="flex flex-col rounded-md bg-muted/40 p-3 gap-1">
                <span className="text-muted-foreground text-xs">Daily solar avg</span>
                <span className="font-semibold tabular-nums text-base">{stats.dailyAvgSolarKwh.toLocaleString()} kWh</span>
              </div>
              <div className="flex flex-col rounded-md bg-muted/40 p-3 gap-1">
                <span className="text-muted-foreground text-xs">Peak solar output</span>
                <span className="font-semibold tabular-nums text-base">{stats.peakSolarKw == null ? 'N/A' : `${stats.peakSolarKw.toLocaleString()} kW`}</span>
              </div>
              <div className="flex flex-col rounded-md bg-muted/40 p-3 gap-1">
                <span className="text-muted-foreground text-xs">Trend vs prior</span>
                <span
                  className={cn(
                    "font-semibold tabular-nums text-base",
                    stats.trendVsPrior > 0 ? "text-[#8FB560]" : stats.trendVsPrior < 0 ? "text-amber-500" : "text-muted-foreground"
                  )}
                >
                  {stats.trendVsPrior > 0 ? '+' : ''}{stats.trendVsPrior.toFixed(1)}%
                </span>
              </div>
              <div className="flex flex-col rounded-md bg-muted/40 p-3 gap-1">
                <span className="text-muted-foreground text-xs">Best period</span>
                <span className="font-semibold tabular-nums text-base">
                  {stats.bestPeriod.label} • {stats.bestPeriod.value}%
                </span>
              </div>
              <div className="flex flex-col rounded-md bg-muted/40 p-3 gap-1">
                <span className="text-muted-foreground text-xs">Lowest period</span>
                <span className="font-semibold tabular-nums text-base">
                  {stats.worstPeriod.label} • {stats.worstPeriod.value}%
                </span>
              </div>
              <div className="flex flex-col rounded-md bg-muted/40 p-3 gap-1">
                <span className="text-muted-foreground text-xs">Period days</span>
                <span className="font-semibold tabular-nums text-base">{stats.daysInPeriod}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="h-full">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Zap className="h-4 w-4 text-[#8FB560]" />
              Energy Mix & Financials
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div className="flex flex-col rounded-md bg-muted/40 p-3 gap-1">
                <span className="text-muted-foreground text-xs">Solar</span>
                <span className="font-medium tabular-nums text-base">
                  {formatKwh(energyMix.solarKwh)}
                  <span className="text-muted-foreground text-xs ml-1">({energyMix.solarPercent}%)</span>
                </span>
              </div>
              <div className="flex flex-col rounded-md bg-muted/40 p-3 gap-1">
                <span className="text-muted-foreground text-xs">Grid</span>
                <span className="font-medium tabular-nums text-base">
                  {formatKwh(energyMix.gridKwh)}
                  <span className="text-muted-foreground text-xs ml-1">({energyMix.gridPercent}%)</span>
                </span>
              </div>
              <div className="flex flex-col rounded-md bg-muted/40 p-3 gap-1">
                <span className="text-muted-foreground text-xs">Generator</span>
                <span className="font-medium tabular-nums text-base">
                  {formatKwh(energyMix.generatorKwh)}
                  <span className="text-muted-foreground text-xs ml-1">({energyMix.generatorPercent}%)</span>
                </span>
              </div>
              <div className="flex flex-col rounded-md bg-muted/40 p-3 gap-1">
                <span className="text-muted-foreground text-xs">Cost Saved</span>
                <span className="font-semibold tabular-nums text-base">
                  ${stats.totalCostSaved.toLocaleString()}
                </span>
              </div>
              <div className="flex flex-col rounded-md bg-muted/40 p-3 gap-1">
                <span className="text-muted-foreground text-xs">Grid Sell</span>
                <span className="font-semibold tabular-nums text-base">
                  {gridSellRevenue > 0 ? `$${gridSellRevenue.toLocaleString()}` : '—'}
                </span>
              </div>
              <div className="flex flex-col rounded-md bg-muted/40 p-3 gap-1">
                <span className="text-muted-foreground text-xs">Total Profit</span>
                <span className="font-bold tabular-nums text-base text-[#8FB560]">
                  ${totalProfit.toLocaleString()}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Energy Totals - Full Metrics Grid */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-xs font-medium uppercase tracking-wider text-muted-foreground flex items-center gap-2">
            <Zap className="h-4 w-4 text-[#8FB560]" />
            Energy Totals
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-x-6 gap-y-2">
            {/* Row 1 - Solar | Grid | Battery headers */}
            <div className="flex justify-between py-1.5 border-b border-border/50">
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Sun className="h-3 w-3 text-[#8FB560]" />
                Solar
              </span>
              <span className="text-sm font-medium tabular-nums">
                {stats.totalSolarKwh.toLocaleString()} kWh
              </span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-border/50">
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <ArrowDownToLine className="h-3 w-3 text-[#8FB560]" />
                Grid In
              </span>
              <span className="text-sm font-medium tabular-nums">
                {stats.totalGridImportKwh?.toLocaleString() || stats.totalGridKwh.toLocaleString()} kWh
              </span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-border/50">
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Battery className="h-3 w-3 text-[#8FB560]" />
                Avg SOC
              </span>
              <span className="text-sm font-medium tabular-nums">
                {stats.avgBatterySoc !== undefined ? `${stats.avgBatterySoc}%` : '—'}
              </span>
            </div>

            {/* Row 2 - Gen | Grid Out | Batt In */}
            <div className="flex justify-between py-1.5 border-b border-border/50">
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Fuel className="h-3 w-3 text-[#8FB560]" />
                Generator
              </span>
              <span className="text-sm font-medium tabular-nums">
                {stats.totalGeneratorKwh?.toLocaleString() || '0'} kWh
              </span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-border/50">
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <ArrowUpFromLine className="h-3 w-3 text-[#8FB560]" />
                Grid Out
              </span>
              <span className="text-sm font-medium tabular-nums">
                {stats.totalGridExportKwh?.toLocaleString() || '0'} kWh
              </span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-border/50">
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Battery className="h-3 w-3 text-[#8FB560]" />
                Batt In
              </span>
              <span className="text-sm font-medium tabular-nums">
                {stats.totalBatteryChargeKwh?.toLocaleString() || '—'} kWh
              </span>
            </div>

            {/* Row 3 - Load | CO2 | Batt Out */}
            <div className="flex justify-between py-1.5">
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Zap className="h-3 w-3 text-[#8FB560]" />
                Load
              </span>
              <span className="text-sm font-medium tabular-nums">
                {stats.totalLoadKwh?.toLocaleString() || '—'} kWh
              </span>
            </div>
            <div className="flex justify-between py-1.5">
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Leaf className="h-3 w-3 text-[#8FB560]" />
                CO₂
              </span>
              <span className="text-sm font-medium tabular-nums">
                {stats.totalCo2EmissionKg?.toLocaleString() || stats.totalCO2.toLocaleString()} kg
              </span>
            </div>
            <div className="flex justify-between py-1.5">
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Battery className="h-3 w-3 text-[#8FB560]" />
                Batt Out
              </span>
              <span className="text-sm font-medium tabular-nums">
                {stats.totalBatteryDischargeKwh?.toLocaleString() || '—'} kWh
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sustainability Section */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-xs font-medium uppercase tracking-wider text-muted-foreground flex items-center gap-2">
            <Leaf className="h-4 w-4 text-[#8FB560]" />
            Sustainability Impact
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {/* Trees Equivalent */}
            <div className="flex flex-col items-center text-center gap-2 p-3 rounded-lg bg-emerald-500/10 min-h-[104px]">
              <TreePine className="h-6 w-6 text-emerald-500" />
              <div className="text-lg font-bold tabular-nums">{stats.treesEquivalent.toLocaleString()}</div>
              <div className="text-xs text-muted-foreground">trees equivalent</div>
            </div>

            {/* Diesel Reduced */}
            <div className="flex flex-col items-center text-center gap-2 p-3 rounded-lg bg-amber-500/10 min-h-[104px]">
              <Fuel className="h-6 w-6 text-amber-500" />
              <div className="text-lg font-bold tabular-nums">{stats.dieselGallonsReduced.toLocaleString()}</div>
              <div className="text-xs text-muted-foreground">gal diesel reduced</div>
            </div>

            {/* Gen Hours Saved */}
            <div className="flex flex-col items-center text-center gap-2 p-3 rounded-lg bg-[#8FB560]/10 min-h-[104px]">
              <Clock className="h-6 w-6 text-[#8FB560]" />
              <div className="text-lg font-bold tabular-nums">{stats.genHoursReduced == null ? 'N/A' : stats.genHoursReduced.toLocaleString()}</div>
              <div className="text-xs text-muted-foreground">gen-hours saved</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Compact 2-Column Table - No header */}
      <Card>
        <CardContent className="p-0">
          <div className="grid grid-cols-2 divide-x divide-border">
            {/* Financial Column - grouped: summary, income, expense, energy */}
            <div>
              <div className="px-4 py-2 bg-muted/30 text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5 border-b border-border">
                <DollarSign className="h-3.5 w-3.5 text-[#8FB560]" />
                Financial
              </div>
              <div className="divide-y divide-border/50">
                <div className="px-4 py-2 flex justify-between items-center">
                  <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                    <DollarSign className="h-3 w-3" />
                    Net Value
                  </span>
                  <span className="text-sm font-semibold tabular-nums">
                    ${Math.abs(Math.round(totalValue)).toLocaleString()}
                  </span>
                </div>
                <div className="px-4 py-2 flex justify-between items-center">
                  <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                    <Sun className="h-3 w-3" />
                    Solar Savings
                  </span>
                  <span className="text-sm font-semibold tabular-nums">
                    ${stats.totalCostSaved.toLocaleString()}
                  </span>
                </div>
                <div className="px-4 py-2 flex justify-between items-center">
                  <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                    <CircleDollarSign className="h-3 w-3" />
                    Grid Sell
                  </span>
                  <span className="text-sm font-semibold tabular-nums">
                    ${Math.round(gridSellRevenue).toLocaleString()}
                  </span>
                </div>
                <div className="px-4 py-2 flex justify-between items-center">
                  <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                    <ArrowDownToLine className="h-3 w-3" />
                    Grid Cost
                  </span>
                  <span className="text-sm font-semibold tabular-nums">
                    −${gridCost.toLocaleString()}
                  </span>
                </div>
                <div className="px-4 py-2 flex justify-between items-center">
                  <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                    <ArrowUpFromLine className="h-3 w-3" />
                    Grid Export
                  </span>
                  <span className="text-sm font-semibold tabular-nums">
                    {formatKwh(stats.totalGridExportKwh ?? 0)}
                  </span>
                </div>
                <div className="px-4 py-2 flex justify-between items-center">
                  <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                    <ArrowDownToLine className="h-3 w-3" />
                    Grid Imported
                  </span>
                  <span className="text-sm font-semibold tabular-nums">
                    {formatKwh(stats.totalGridImportKwh ?? stats.totalGridKwh ?? 0)}
                  </span>
                </div>
              </div>
            </div>

            {/* Operations Column - grouped: energy, grid, fleet */}
            <div>
              <div className="px-4 py-2 bg-muted/30 text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5 border-b border-border">
                <Cog className="h-3.5 w-3.5 text-[#8FB560]" />
                Operations
              </div>
              <div className="divide-y divide-border/50">
                <div className="px-4 py-2 flex justify-between items-center">
                  <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                    <Sun className="h-3 w-3" />
                    Daily Avg Solar
                  </span>
                  <span className="text-sm font-semibold tabular-nums">
                    {stats.dailyAvgSolarKwh.toLocaleString()} kWh
                  </span>
                </div>
                <div className="px-4 py-2 flex justify-between items-center">
                  <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                    <Zap className="h-3 w-3" />
                    Total Load
                  </span>
                  <span className="text-sm font-semibold tabular-nums">
                    {formatKwh(stats.totalLoadKwh ?? 0)}
                  </span>
                </div>
                <div className="px-4 py-2 flex justify-between items-center">
                  <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                    <Unplug className="h-3 w-3" />
                    Grid Dependency
                  </span>
                  <span className="text-sm font-semibold tabular-nums">
                    {gridDependency}%
                  </span>
                </div>
                <div className="px-4 py-2 flex justify-between items-center">
                  <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                    {stats.trendVsPrior >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    Period Trend
                  </span>
                  <span className="text-sm font-semibold tabular-nums">
                    {stats.trendVsPrior > 0 ? '+' : ''}{stats.trendVsPrior.toFixed(1)}%
                  </span>
                </div>
                <div className="px-4 py-2 flex justify-between items-center">
                  <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                    <Truck className="h-3 w-3" />
                    Units Active
                  </span>
                  <span className="text-sm font-semibold tabular-nums">
                    {maxUnitsActive.toLocaleString()}
                  </span>
                </div>
                <div className="px-4 py-2 flex justify-between items-center">
                  <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                    <Battery className="h-3 w-3" />
                    Avg Battery SOC
                  </span>
                  <span className="text-sm font-semibold tabular-nums">
                    {avgBatterySoc}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Battery & Generator - Full width row */}
          <div className="border-t border-border">
            <div className="px-4 py-2 bg-muted/30 text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
              <Battery className="h-3.5 w-3.5 text-[#8FB560]" />
              Battery & Generator
            </div>
            <div className="grid grid-cols-2 gap-y-1 sm:grid-cols-4 sm:gap-y-0 sm:divide-x sm:divide-border/50">
              <div className="px-4 py-2 text-center">
                <div className="text-xs text-muted-foreground">Battery Charged</div>
                <div className="text-sm font-semibold tabular-nums">{formatKwh(stats.totalBatteryChargeKwh ?? 0)}</div>
              </div>
              <div className="px-4 py-2 text-center">
                <div className="text-xs text-muted-foreground">Battery Discharged</div>
                <div className="text-sm font-semibold tabular-nums">{formatKwh(stats.totalBatteryDischargeKwh ?? 0)}</div>
              </div>
              <div className="px-4 py-2 text-center">
                <div className="text-xs text-muted-foreground">Generator Used</div>
                <div className="text-sm font-semibold tabular-nums">{formatKwh(stats.totalGeneratorKwh ?? 0)}</div>
              </div>
              <div className="px-4 py-2 text-center">
                <div className="text-xs text-muted-foreground">Avg Daily Load</div>
                <div className="text-sm font-semibold tabular-nums">
                  {stats.daysInPeriod > 0 ? formatKwh(Math.round((stats.totalLoadKwh ?? 0) / stats.daysInPeriod)) : '—'}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
